import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isDefinitiveAuthError } from '@/utils/authErrors';

/**
 * Point UNIQUE de rafraîchissement et de déconnexion de session.
 *
 * Le refresh token Supabase est à usage unique : deux `refreshSession()`
 * simultanés (auto-refresh, démarrage, reprise Capacitor, 401 d'un service)
 * font échouer le second en « already used / not found », ce qui déconnectait
 * l'utilisateur à tort. Ici, tous les appelants partagent la même opération.
 */

let inflight: Promise<Session | null> | null = null;
let intentionalSignOut = false;

const REFRESH_MARGIN_MS = 5 * 60 * 1000;

async function readStoredSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/** Rafraîchit la session (dédupliqué). Ne déconnecte jamais. */
export function refreshSessionShared(): Promise<Session | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) return data.session;
      // Un autre onglet / appel a peut-être déjà fait tourner le jeton.
      const stored = await readStoredSession();
      if (stored && stored.expires_at && stored.expires_at * 1000 > Date.now()) return stored;
      if (error) console.warn('[sessionManager] refresh failed:', error.message);
      return null;
    } catch (e) {
      console.warn('[sessionManager] refresh threw:', e);
      return null;
    } finally {
      // Libère le verrou au tick suivant pour absorber les appels en rafale.
      setTimeout(() => { inflight = null; }, 0);
    }
  })();
  return inflight;
}

/** Garantit une session valide ; ne rafraîchit que si elle expire bientôt. */
export async function ensureFreshSession(): Promise<Session | null> {
  const session = await readStoredSession();
  if (!session) return null;
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt - Date.now() > REFRESH_MARGIN_MS) return session;
  return (await refreshSessionShared()) ?? session;
}

/**
 * Confirme côté serveur que la session est réellement révoquée.
 * Renvoie `true` UNIQUEMENT sur une erreur d'auth définitive après refresh.
 */
export async function isSessionDefinitivelyInvalid(): Promise<boolean> {
  try {
    const stored = await readStoredSession();
    if (!stored) {
      // Lecture vide possible juste après un réveil (stockage natif async).
      await new Promise((r) => setTimeout(r, 800));
      if (!(await readStoredSession())) return true;
    }
    const refreshed = await refreshSessionShared();
    if (refreshed) return false;
    const { error } = await supabase.auth.getUser();
    if (!error) return false;
    return isDefinitiveAuthError(error);
  } catch {
    return false; // ambigu → on garde la session
  }
}

/** Déconnexion LOCALE (les autres appareils restent connectés). */
export async function signOutLocal(): Promise<void> {
  intentionalSignOut = true;
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (e) {
    console.warn('[sessionManager] local signOut failed:', e);
  }
}

/** Indique si le dernier SIGNED_OUT a été demandé par l'app elle-même. */
export function consumeIntentionalSignOut(): boolean {
  const v = intentionalSignOut;
  intentionalSignOut = false;
  return v;
}

/** Réservé aux tests. */
export function __resetSessionManagerForTests(): void {
  inflight = null;
  intentionalSignOut = false;
}
